<template>
    <b-container fluid>
        <b-row>
            <b-col cols="12">
                <div class="border-bottom mb-3 pb-3">
                    <h3>템플릿 시뮬레이션</h3>
                    <p class="fw-400 text-secondary mb-0">조건을 넣으면 어떤 템플릿이 선택되는지 확인합니다. 메일은 발송되지 않습니다.</p>
                </div>
            </b-col>
        </b-row>

        <!-- 입력 -->
        <b-row class="mb-3"><b-col cols="12">
        <b-card no-body bg-variant="white" class="p-3">
            <b-form-row>
                <b-col cols="12" md="4">
                    <b-form-group label="시나리오" label-class="small mb-1">
                        <b-form-select v-model="form.scenario_code" :options="scenarioOptions" size="sm" :disabled="loading" />
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                    <b-form-group label="브랜드 (서브도메인)" label-class="small mb-1">
                        <ConditionValuePicker v-model="form.sub_domain" entity="brand" :multiple="false" :disabled="loading" />
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                    <b-form-group label="언어" label-class="small mb-1">
                        <b-form-select v-model="form.locale" :options="localeOptions" size="sm" :disabled="loading" />
                    </b-form-group>
                </b-col>
                <b-col
                    v-for="field in pickerFields"
                    :key="field.key"
                    cols="6"
                    md="3"
                >
                    <b-form-group :label="field.label" label-class="small mb-1">
                        <ConditionValuePicker v-model="form[field.key]" :entity="field.key" :multiple="false" :disabled="loading" />
                    </b-form-group>
                </b-col>
            </b-form-row>
            <div class="text-right">
                <b-button variant="primary" size="sm" :disabled="!form.scenario_code || loading" @click="runSimulation">
                    <b-spinner v-if="loading" small class="mr-1" />
                    시뮬레이션 실행
                </b-button>
            </div>
        </b-card>
        </b-col></b-row>

        <template v-if="result">
            <!-- 1) 판정 -->
            <b-alert v-if="result.ok" show variant="success">이 조건이면 아래 템플릿이 선택됩니다</b-alert>
            <b-alert v-else show variant="danger">
                <div class="font-weight-bold">템플릿을 선택할 수 없습니다</div>
                <ul class="mb-0 mt-1">
                    <li v-for="(err, i) in (result.errors || [])" :key="'err-' + i">
                        [{{ stageLabel(err.stage) }}] {{ err.message }}
                    </li>
                </ul>
            </b-alert>

            <!-- 2) 매칭 결과 -->
            <b-card bg-variant="white" class="mb-3" header="매칭 결과">
                <div class="small mb-3">
                    <span class="font-weight-bold">입력 조건</span>
                    <span v-if="!Object.keys(result.contexts || {}).length" class="text-muted ml-1">없음</span>
                    <b-badge v-for="(v, k) in result.contexts" :key="k" variant="light" class="ml-1 text-wrap" :title="`${contextKeyLabel(k)}: ${v}`">{{ contextKeyLabel(k) }}: {{ conditionValueText(k, v) }}</b-badge>
                </div>
                <div v-if="result.matched_template" class="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <b>#{{ result.matched_template.id }} {{ result.matched_template.label }}</b>
                        <span v-if="!Object.keys(result.matched_template.contexts || {}).length" class="text-muted ml-1">조건 없음 (기본)</span>
                        <b-badge v-for="(v, k) in result.matched_template.contexts" :key="k" variant="light" class="ml-1 text-wrap" :title="`${contextKeyLabel(k)}: ${v}`">{{ contextKeyLabel(k) }}: {{ conditionValueText(k, v) }}</b-badge>
                    </div>
                    <b-button
                        variant="outline-primary"
                        size="sm"
                        :to="{ name: 'MailEditorEdit', params: { id: result.matched_template.id } }"
                    >편집기에서 열기</b-button>
                </div>
                <p v-else class="text-muted">매칭된 템플릿이 없습니다.</p>
                <b-table :items="searchSteps" :fields="stepFields" small responsive show-empty empty-text="탐색 기록이 없습니다.">
                    <template #cell(combination)="{ item }">
                        <span v-if="!(item.combination || []).length" class="text-muted">조건 없음</span>
                        <span v-else>{{ item.combination.map(contextKeyLabel).join(' + ') }}</span>
                    </template>
                    <template #cell(search_contexts)="{ item }">
                        <b-badge v-for="(v, k) in item.search_contexts" :key="k" variant="light" class="mr-1">{{ k }}={{ v }}</b-badge>
                    </template>
                    <template #cell(result)="{ item }">
                        <b-badge :variant="item.result === 'not_found' ? 'light' : 'success'">{{ stepResultLabel(item.result) }}</b-badge>
                    </template>
                </b-table>
            </b-card>

            <!-- 3) 콘텐츠 -->
            <b-card v-if="result.content" bg-variant="white" class="mb-3" header="콘텐츠">
                <b-badge variant="light" class="mr-1">언어: {{ result.content.locale }}</b-badge>
                <b-badge v-if="result.content.is_default" variant="info" class="mr-1">기본 콘텐츠</b-badge>
                <b-badge v-if="result.content.requested_locale" variant="light" class="mr-1">요청 언어: {{ result.content.requested_locale }}</b-badge>
                <b-badge v-if="result.content.fell_back" variant="warning">요청 언어 없음 → 기본 콘텐츠</b-badge>
            </b-card>

            <!-- 4) 미리보기: 편집기 미리보기와 같은 캔버스(변수는 칩) -->
            <b-card bg-variant="white" class="mb-3" header="미리보기">
                <div class="mb-2">
                    <span class="small font-weight-bold">제목: </span>
                    <span v-if="result.subject != null">{{ result.subject }}</span>
                    <span v-else class="text-muted">(만들지 못함)</span>
                </div>
                <MailDocCanvas v-if="result.body_html != null" :value="result.body_html" preview />
                <p v-else class="text-muted mb-0">본문을 만들지 못했습니다.</p>
            </b-card>
        </template>
    </b-container>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import { CONTEXT_KEYS, LOCALE_OPTIONS, sortScenarios } from '../MailEditor/data';
import MailDocCanvas from '../MailEditor/_SubComponent/MailDocCanvas.vue';
import ConditionValuePicker from '../_common/ConditionValuePicker.vue';
import conditionLabels from '../_common/conditionLabels';
import mailSimulation from './mailSimulation';

const STAGE_LABELS = {
    match: '템플릿 매칭',
    content: '콘텐츠 선택',
    snippet: '스니펫 전개',
};

const STEP_RESULT_LABELS = {
    'found': '매칭',
    'found (default fallback)': '기본 템플릿 매칭',
    'not_found': '없음',
};

export default {
    name: 'MailSimulation',
    components: { ConditionValuePicker, MailDocCanvas },
    data() {
        return {
            loading: false,
            scenarios: [],
            form: {
                scenario_code: '',
                sub_domain: '',
                project: '',
                campaign: '',
                company: '',
                user: '',
                locale: '',
            },
            pickerFields: [
                { key: 'project', label: '프로젝트 코드' },
                { key: 'campaign', label: '캠페인' },
                { key: 'company', label: '기업' },
                { key: 'user', label: '사용자' },
            ],
            result: null,
            // 조건값 토큰 → { label, sub } | null(찾을 수 없음). 목록 화면과 같은 방식으로 ID 를 이름으로 보여 준다.
            conditionNames: {},
            conditionLookupForbidden: false,
            stepFields: [
                { key: 'combination', label: '조합' },
                { key: 'search_contexts', label: '검색 조건' },
                { key: 'result', label: '결과' },
                { key: 'matched_template_id', label: '템플릿 id' },
            ],
        };
    },
    computed: {
        scenarioOptions() {
            const opts = [{ value: '', text: '시나리오 선택' }];
            this.scenarios
                .filter(s => mailSimulation.isSelectableScenario(s.code))
                .forEach(s => opts.push({ value: s.code, text: s.label || s.code }));
            return opts;
        },
        localeOptions() {
            return [{ value: '', text: '지정 안 함' }, ...LOCALE_OPTIONS];
        },
        searchSteps() {
            return (this.result && this.result.search_process && this.result.search_process.search_steps) || [];
        },
    },
    mounted() {
        this.loadScenarios();
    },
    methods: {
        async loadScenarios() {
            try {
                const res = await this.$axios.get('/mail/scenario');
                this.scenarios = sortScenarios(res.data || []);
            } catch (e) {
                this.$EventBus.$emit('toast-failure', '오류', '시나리오 목록을 불러올 수 없습니다.');
            }
        },
        async runSimulation() {
            this.loading = true;
            this.result = null;
            try {
                const res = await this.$axios.post('/mail/template/dry-run', mailSimulation.buildDryRunPayload(this.form));
                this.result = res.data;
                this.resolveConditionLabels();
            } catch (e) {
                const msg = e.response?.status === 403
                    ? '슈퍼 관리자만 사용할 수 있습니다'
                    : getErrorMessage(e, '시뮬레이션에 실패했습니다.');
                this.$EventBus.$emit('toast-failure', '오류', msg);
            } finally {
                this.loading = false;
            }
        },
        contextKeyLabel(k) {
            const found = CONTEXT_KEYS.find(c => c.value === k);
            return found ? found.label : k;
        },
        entityOf(k) {
            const found = CONTEXT_KEYS.find(c => c.value === k);
            return found ? found.entity : '';
        },
        conditionValueText(k, v) {
            return conditionLabels.conditionValueText(this.entityOf(k), v, this.conditionNames);
        },
        // 입력 조건·선택된 템플릿 조건의 ID 를 엔티티별 한 번씩만 물어 이름으로 바꾼다. 실패하면 원문 그대로 둔다.
        async resolveConditionLabels() {
            if (this.conditionLookupForbidden || !this.result) return;
            const rows = [{ contexts: this.result.contexts }];
            if (this.result.matched_template) rows.push({ contexts: this.result.matched_template.contexts });
            const requests = conditionLabels.collectLookupRequests(rows, this.entityOf, this.conditionNames);
            await Promise.all(requests.map(async ({ entity, ids }) => {
                try {
                    const { data } = await this.$axios.get(`/mail/lookup/${entity}`, { params: { ids: ids.join(',') } });
                    const found = {};
                    ((data && data.items) || []).forEach(item => {
                        found[item.value] = { label: item.label, sub: item.sub };
                    });
                    const delta = {};
                    ids.forEach(id => {
                        delta[conditionLabels.labelKey(entity, id)] = found[id] || null;
                    });
                    this.conditionNames = { ...this.conditionNames, ...delta };
                } catch (e) {
                    if (e && e.response && e.response.status === 403) this.conditionLookupForbidden = true;
                }
            }));
        },
        stageLabel(stage) {
            return STAGE_LABELS[stage] || stage;
        },
        stepResultLabel(r) {
            return STEP_RESULT_LABELS[r] || r;
        },
    },
};
</script>
